import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal, Dropdown } from 'react-bootstrap'

import { httpOk, GET, POST, PUT, DELETE } from 'helpers/api'
import DeleteModal from 'pages/parts/DeleteModal'
import HintIcon from 'pages/parts/HintIcon'

// Цвета на выбор: свободный выбор цвета в каждой метке разваливает витрину на оттенки.
// Иконок нет: гость видит метку цветом и словом, как в Яндекс Еде и Wolt
const LABEL_COLORS = ['#16a34a', '#65a30d', '#0ea5e9', '#2563eb', '#7c3aed', '#db2777', '#dc2626', '#f97316', '#ca8a04', '#475569']

const EMPTY_LABEL = { names: {}, color: LABEL_COLORS[0], icon: null }

// Название метки на языке кабинета; нет такого — русское (оно обязательно)
export function productLabelTitle(label, lang) {
	const names = label?.names ?? {}
	const code = String(lang || 'ru').toLowerCase().replace('_', '-')
	return names[code] || names.ru || Object.values(names).find(Boolean) || ''
}

// Метка так, как её увидит гость на карточке блюда
export function ProductLabelBadge({ label, name }) {
	const { i18n } = useTranslation()
	return (
		<span className="d-inline-flex align-items-center"
			style={{ background: label.color, color: '#fff', borderRadius: '999px', padding: '2px 10px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
			{name ?? productLabelTitle(label, i18n.language)}
		</span>
	)
}

function Index() {
	const { t, i18n } = useTranslation()

	const [data, setData] = useState([])
	const [languages, setLanguages] = useState([{ code: 'ru', name: 'Русский' }])
	const [selectedItem, setSelectedItem] = useState(EMPTY_LABEL)
	const [showModal, setShowModal] = useState(false)
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteId, setDeleteId] = useState(null)
	const [translating, setTranslating] = useState(false)

	async function searchWithFilter() {
		const response = await GET('/product-labels', {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data.data ?? [])
			if (response.data.languages?.length) setLanguages(response.data.languages)
		}
	}

	async function createUpdate(e) {
		e.preventDefault()

		var response
		if (selectedItem.id) {
			response = await PUT('/product-labels', selectedItem, { loader: true })
		} else {
			response = await POST('/product-labels', selectedItem, { loader: true })
		}

		if (httpOk(response)) {
			searchWithFilter()
			setShowModal(false)
		}
	}

	// Менеджер пишет на языке, который знает, — пустые поля заполняет ИИ; результат можно поправить
	async function translateNames() {
		const names = selectedItem.names ?? {}
		const source = languages.find((lang) => (names[lang.code] ?? '').trim())
		if (!source) return
		const targets = languages.filter((lang) => !(names[lang.code] ?? '').trim()).map((lang) => lang.code)
		if (targets.length === 0) return

		setTranslating(true)
		const response = await POST('/ai/translate', {
			text: names[source.code],
			from: source.code,
			to: targets,
			context: 'short marketing label on a menu item card, 1-2 words',
		}, { notification: false })
		setTranslating(false)

		if (httpOk(response)) {
			setSelectedItem((prev) => ({ ...prev, names: { ...response.data.translations, ...prev.names } }))
		}
	}

	async function deleteItem() {
		const response = await DELETE(`/product-labels/${deleteId}`, {}, { loader: true })
		if (httpOk(response)) {
			setModalDelete(false)
			searchWithFilter()
		}
	}

	function setName(code, value) {
		setSelectedItem({ ...selectedItem, names: { ...(selectedItem.names ?? {}), [code]: value } })
	}

	function toggleModal(bool = false, item = EMPTY_LABEL) {
		setSelectedItem({ ...EMPTY_LABEL, ...item, icon: null, names: { ...(item.names ?? {}) } })
		setShowModal(bool)
	}

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const names = selectedItem.names ?? {}
	const canTranslate = languages.length > 1
		&& languages.some((lang) => (names[lang.code] ?? '').trim())
		&& languages.some((lang) => !(names[lang.code] ?? '').trim())
	const otherLanguages = languages.filter((lang) => lang.code !== 'ru')

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					<span>
						{t('product_labels')}
						<HintIcon text={t('product_labels_hint')} />
					</span>
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary"
						onClick={() => toggleModal(true)}>
						{t('create')}
					</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'width': '33px', 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '20%' }}>{t('label_view')}</th>
									{otherLanguages.map((lang) => (
										<th key={lang.code}>{lang.name}</th>
									))}
									<th style={{ 'width': '10%' }} className="text-center">{t('products')}</th>
									<th style={{ 'width': '33px', 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data.map((item, index) => (
									<tr key={item.id}>
										<td className="text-center">{index + 1}</td>
										<td><ProductLabelBadge label={item} name={item.names?.ru} /></td>
										{otherLanguages.map((lang) => (
											<td key={lang.code}>{item.names?.[lang.code]}</td>
										))}
										<td className="text-center">{item.products_count}</td>
										<td>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														<div className="table-dropdown-item"
															onClick={() => toggleModal(true, item)}>
															<i className="uil-edit-alt"></i>
															{t('edit')}
														</div>
														<div className="table-dropdown-item text-danger"
															onClick={() => { setDeleteId(item.id); setModalDelete(true) }}>
															<i className="uil-trash-alt"></i>
															{t('delete')}
														</div>
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* MODAL */}
			<Modal show={showModal} animation={false} centered
				dialogClassName="update-modal-width" onHide={() => toggleModal()}>
				<Modal.Header closeButton>
					<Modal.Title>
						{!selectedItem.id ? t('create') : t('edit')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={createUpdate} autoComplete="off">
						<div className="d-flex justify-content-between align-items-center mb-1">
							<label className="mb-0">
								{t('label_names')}
								<HintIcon text={t('label_names_hint')} />
							</label>
							{languages.length > 1 &&
								<button type="button" className="btn btn-sm btn-outline-primary"
									disabled={!canTranslate || translating}
									title={t('ai_translate_hint')}
									onClick={translateNames}>
									<i className="uil-language me-1"></i>
									{translating ? t('ai_translating') : t('ai_translate')}
								</button>
							}
						</div>
						{/* Названия по языкам каналов бренда — по два в ряд */}
						<div className="row">
							{languages.map((lang, index) => (
								<div key={lang.code} className="col-6 form-group">
									<label>
										{lang.name}
										{lang.code === 'ru' && <span className="required-mark">*</span>}
									</label>
									<input type="text" className="form-control" maxLength={50}
										autoFocus={index === 0}
										value={names[lang.code] ?? ''}
										onChange={(e) => setName(lang.code, e.target.value)} />
								</div>
							))}
						</div>

						<div className="form-group">
							<label>{t('label_color')}</label>
							<div className="d-flex flex-wrap gap-2">
								{LABEL_COLORS.map((color) => (
									<button type="button" key={color} title={color}
										onClick={() => setSelectedItem({ ...selectedItem, color })}
										style={{
											width: '28px', height: '28px', borderRadius: '50%', background: color, cursor: 'pointer', border: 'none',
											// Выбранный — кольцо того же цвета через белый зазор, а не тёмная обводка поверх заливки
											boxShadow: selectedItem.color === color ? `0 0 0 2px var(--dark-bg, #fff), 0 0 0 4px ${color}` : 'none',
										}} />
								))}
							</div>
						</div>

						<div className="form-group">
							<label>{t('label_preview')}</label>
							<div>
								<ProductLabelBadge label={selectedItem} name={productLabelTitle(selectedItem, i18n.language) || t('name2')} />
							</div>
						</div>

						<div className="d-flex gap-2 w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModal()}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100">
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL */}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}
				message={t('product_label_delete_confirm')}
			/>
		</>
	)
}

export default Index
